import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db, auth } from '@/config/firebase';
import { BillingEvent } from '@/types';
import { BillingRecord } from '@/types';
import { logger } from '../utils/logger';
import { withErrorHandling } from '@/utils/errorHandling';

import { Config } from '@/config/env';

// Get Cloud Functions base URL dynamically from environment
const FUNCTIONS_BASE_URL = `https://us-central1-${Config.firebase.projectId}.cloudfunctions.net`;

/**
 * Helper function to get auth token and make authenticated request
 */
async function callCloudFunction<T>(
  functionName: string,
  data: Record<string, unknown>
): Promise<T> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  try {
    const idToken = await user.getIdToken();

    const response = await fetch(`${FUNCTIONS_BASE_URL}/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

      // Create error with context for logging
      const errorContext = {
        functionName,
        status: response.status,
        statusText: response.statusText,
        errorData,
      };

      // Provide more specific error messages for common Stripe failures
      let userFriendlyError: Error & { diagnostic?: Record<string, unknown> };
      if (response.status === 401) {
        userFriendlyError = new Error('Authentication failed. Please refresh the page and try again.');
      } else if (response.status === 400) {
        // Bad request - likely invalid payment method or Stripe error
        // Backend now provides detailed, actionable error messages
        const errorMsg = errorData.error || 'Invalid payment information. Please check your card details and try again.';
        userFriendlyError = new Error(errorMsg);
        // Preserve diagnostic info if present (development mode)
        if (errorData.diagnostic) {
          userFriendlyError.diagnostic = errorData.diagnostic;
        }
      } else if (response.status === 402) {
        // Payment required - card declined
        userFriendlyError = new Error('Payment declined. Please check your card details or try a different payment method.');
      } else if (response.status === 403) {
        userFriendlyError = new Error('Access denied. Please ensure you have permission to set up billing for this account.');
      } else if (response.status === 500) {
        // Prefer the backend's error message if available, otherwise use generic
        const errorMsg = errorData.error || 'Server error. Please try again in a few moments. If the problem persists, contact support.';
        userFriendlyError = new Error(errorMsg);
      } else {
        userFriendlyError = new Error(errorData.error || `Request failed (${response.status}): ${response.statusText}`);
      }

      // Log error with context for debugging
      logger.error(`Cloud function error: ${functionName}`, userFriendlyError, errorContext);

      throw userFriendlyError;
    }

    const result = await response.json();
    return result.result || result;
  } catch (error: unknown) {
    // Handle network errors and other fetch failures
    if (!(error instanceof Error && error.message.includes('status'))) {
      // This is likely a network error, CORS error, CSP violation, or JSON parsing error
      const errorContext = {
        functionName,
        firmId: (data as Record<string, unknown>)?.firmId,
        hasPaymentMethodId: !!(data as Record<string, unknown>)?.paymentMethodId,
        errorType: error instanceof Error ? error.constructor.name : 'Unknown',
        errorMessage: error instanceof Error ? error.message : String(error),
      };

      logger.error(`Network/parsing error calling cloud function: ${functionName}`, error instanceof Error ? error : new Error(String(error)), errorContext);

      // Detect specific error types and provide helpful messages
      const errorMessage = (error as Error)?.message || String(error) || '';

      // Check for common network/CSP/CORS issues
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('ERR_BLOCKED_BY_CLIENT') || errorMessage.includes('ERR_NETWORK')) {
        // This could be CSP, CORS, network issue, or browser extension blocking
        const helpfulMessage = 'Connection failed. This might be caused by:\n' +
          '• Browser extensions (ad blockers, privacy tools) blocking requests\n' +
          '• Network connectivity issues\n' +
          '• Content Security Policy restrictions\n\n' +
          'Please try:\n' +
          '1. Disable browser extensions temporarily\n' +
          '2. Check your internet connection\n' +
          '3. Refresh the page and try again\n' +
          '4. If the problem persists, contact support';

        throw new Error(helpfulMessage);
      }

      // Re-throw if already an Error with message, otherwise wrap
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Network error: Failed to call ${functionName}. Please check your connection and try again.`);
    }
    // If error was thrown from the status check above, re-throw it
    throw error;
  }
}

/**
 * Setup payment method for a firm (without creating subscription)
 * Moves firm to 'trial' status - subscription created when first case is added
 */
export async function setupPaymentMethod(
  firmId: string,
  firmName: string,
  ownerEmail: string,
  paymentMethodId: string
): Promise<{ success: boolean; customerId: string; paymentMethodId: string; status: string } | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      customerId: string;
      paymentMethodId: string;
      status: string;
    }>('setupPaymentMethodV2', { firmId, firmName, ownerEmail, paymentMethodId });

    if (!result.success) {
      throw new Error('Failed to setup payment method');
    }

    return {
      success: result.success,
      customerId: result.customerId,
      paymentMethodId: result.paymentMethodId,
      status: result.status,
    };
  }, 'setting up payment method');
}

/**
 * Activate billing when first case is created
 * Creates Stripe subscription and moves firm from 'trial' to 'active' status
 */
export async function activateBilling(
  firmId: string
): Promise<{
  success: boolean;
  subscriptionId: string;
  productId: string;
  priceId: string;
  activeCasesCount: number;
  status: string;
} | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      subscriptionId: string;
      productId: string;
      priceId: string;
      activeCasesCount: number;
      status: string;
      message?: string;
    }>('activateBillingV2', { firmId });

    if (!result.success) {
      throw new Error('Failed to activate billing');
    }

    return {
      success: result.success,
      subscriptionId: result.subscriptionId,
      productId: result.productId,
      priceId: result.priceId,
      activeCasesCount: result.activeCasesCount,
      status: result.status,
    };
  }, 'activating billing');
}

/**
 * Create Stripe customer and subscription for a firm
 * @deprecated Use setupPaymentMethod instead for new implementations
 */
export async function createStripeCustomer(
  firmId: string,
  firmName: string,
  ownerEmail: string
): Promise<{ success: boolean; customerId: string; subscriptionId: string } | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      customerId: string;
      subscriptionId: string;
      productId: string;
      priceId: string;
    }>('createStripeCustomerV2', { firmId, firmName, ownerEmail });

    if (!result.success) {
      throw new Error('Failed to create Stripe customer');
    }

    return {
      success: result.success,
      customerId: result.customerId,
      subscriptionId: result.subscriptionId,
    };
  }, 'creating Stripe customer');
}

/**
 * Update subscription quantity based on active cases
 */
export async function updateSubscriptionQuantity(
  firmId: string
): Promise<{ success: boolean; activeCasesCount: number } | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      activeCasesCount: number;
    }>('updateSubscriptionQuantityV2', { firmId });

    if (!result.success) {
      throw new Error('Subscription update failed');
    }

    return {
      success: result.success,
      activeCasesCount: result.activeCasesCount,
    };
  }, 'updating subscription quantity');
}

/**
 * Get payment method details for a firm
 */
export async function getPaymentMethod(firmId: string): Promise<{
  success: boolean;
  paymentMethod: {
    id: string;
    type: string;
    card?: {
      brand: string;
      last4: string;
      expMonth: number;
      expYear: number;
    };
  } | null;
} | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      paymentMethod: {
        id: string;
        type: string;
        card?: {
          brand: string;
          last4: string;
          expMonth: number;
          expYear: number;
        };
      } | null;
    }>('getPaymentMethodV2', { firmId });

    return {
      success: result.success,
      paymentMethod: result.paymentMethod,
    };
  }, 'fetching payment method');
}

/**
 * Update payment method for a firm
 * Attaches a new payment method and optionally sets it as default
 */
export async function updatePaymentMethod(
  firmId: string,
  paymentMethodId: string
): Promise<{
  success: boolean;
  paymentMethodId: string;
  isDefault: boolean;
} | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      paymentMethodId: string;
      isDefault: boolean;
    }>('updatePaymentMethodV2', { firmId, paymentMethodId });

    return {
      success: result.success,
      paymentMethodId: result.paymentMethodId,
      isDefault: result.isDefault,
    };
  }, 'update payment method', { silent: true });
}

/**
 * List all payment methods for a firm
 */
export async function listPaymentMethods(firmId: string): Promise<{
  success: boolean;
  paymentMethods: Array<{
    id: string;
    type: string;
    card?: {
      brand: string;
      last4: string;
      expMonth: number;
      expYear: number;
    };
    isDefault: boolean;
  }>;
} | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      paymentMethods: Array<{
        id: string;
        type: string;
        card?: {
          brand: string;
          last4: string;
          expMonth: number;
          expYear: number;
        };
        isDefault: boolean;
      }>;
    }>('listPaymentMethodsV2', { firmId });

    return {
      success: result.success,
      paymentMethods: result.paymentMethods,
    };
    // Context is used as an infinitive ("Failed to <context>" / "permission to
    // <context>"), so it must read "list …", not "listing …" (S24-7).
  }, 'list payment methods');
}

/**
 * Set default payment method for a firm
 */
export async function setDefaultPaymentMethod(
  firmId: string,
  paymentMethodId: string
): Promise<{
  success: boolean;
  paymentMethodId: string;
} | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      paymentMethodId: string;
    }>('setDefaultPaymentMethodV2', { firmId, paymentMethodId });

    return {
      success: result.success,
      paymentMethodId: result.paymentMethodId,
    };
  }, 'setting default payment method');
}

/**
 * Get billing history for a firm
 */
export async function getBillingHistory(
  firmId: string,
  // Bound the read for callers that only render a preview (e.g. the Billing
  // dashboard shows the 3 most recent). Ordered createdAt desc, so a limit
  // returns the newest N. Omit for the full history page (s34-4).
  limitCount?: number,
): Promise<BillingRecord[] | undefined> {
  return withErrorHandling(async () => {
    const billingRef = collection(db, `firms/${firmId}/billingRecords`);
    const q = limitCount
      ? query(billingRef, orderBy('createdAt', 'desc'), limit(limitCount))
      : query(billingRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    const records: BillingRecord[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Ensure billingRecordId is set (use doc.id as fallback if not in data)
      const record: BillingRecord = {
        ...data,
        billingRecordId: data.billingRecordId || doc.id,
      } as BillingRecord;
      records.push(record);
    });

    logger.info(`Retrieved ${records.length} billing records for firm ${firmId}`);
    return records;
  }, 'fetching billing history');
}


/**
 * Get recent billing events (audit log)
 */
export async function getRecentBillingEvents(
  firmId: string,
  limitCount: number = 20
): Promise<BillingEvent[]> {
  return (await withErrorHandling(async () => {
    const eventsRef = collection(db, `firms/${firmId}/billingEvents`);
    // Note: This requires an index on createdAt desc
    const q = query(eventsRef, orderBy('createdAt', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);

    const events: BillingEvent[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      events.push({
        billingEventId: doc.id,
        firmId,
        eventType: data.eventType as BillingEvent['eventType'],
        description: data.description || '', // Ensure description is set
        metadata: data.metadata || {},
        performedBy: data.performedBy as BillingEvent['performedBy'],
        createdAt: data.createdAt,
      });
    });

    return events;
  }, 'fetching billing events', { rethrow: false })) || [];
}

/**
 * Remove a payment method (non-default only)
 */
export async function removePaymentMethod(
  firmId: string,
  paymentMethodId: string
): Promise<{ success: boolean; paymentMethodId: string } | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      paymentMethodId: string;
    }>('removePaymentMethodV2', { firmId, paymentMethodId });

    return result;
  }, 'removing payment method');
}

/**
 * Pause subscription for a firm
 */
export async function pauseSubscription(
  firmId: string
): Promise<{ success: boolean } | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
    }>('pauseSubscriptionV2', { firmId });

    if (!result.success) {
      throw new Error('Failed to pause subscription');
    }

    return {
      success: result.success,
    };
  }, 'pausing subscription');
}

/**
 * Request account deletion for a firm
 * Initiates 30-day grace period before permanent deletion
 */
export async function deleteAccount(
  firmId: string
): Promise<{ success: boolean; deletionScheduledAt: string } | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      deletionScheduledAt: string;
    }>('deleteFirmDataV2', { firmId });

    return {
      success: result.success,
      deletionScheduledAt: result.deletionScheduledAt,
    };
  }, 'requesting account deletion');
}

/**
 * Resume a paused subscription
 * Restarts Stripe billing and updates Firestore status to 'active'
 */
export async function resumeSubscription(
  firmId: string
): Promise<{ success: boolean; status: string } | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      status: string;
    }>('resumeSubscriptionV2', { firmId });

    if (!result.success) {
      throw new Error('Failed to resume subscription');
    }

    return {
      success: result.success,
      status: result.status,
    };
  }, 'resuming subscription');
}

/**
 * Cancel scheduled account deletion
 * Restores firm and users to active status during 30-day grace period
 */
export async function cancelAccountDeletion(
  firmId: string
): Promise<{ success: boolean; message: string } | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      message: string;
    }>('cancelDeletionV2', { firmId });

    return {
      success: result.success,
      message: result.message,
    };
  }, 'canceling deletion');
}

// ============================================================
// STATEMENT-BILLING RAIL (see plans/stripe-billing-audit-serialized-flame.md)
// ============================================================

export type FirmBillingMode = 'subscription' | 'monthly_statement';
export type FirmBillingTerms = 'auto_pay' | 'net_30' | 'net_60' | 'net_90';

/**
 * Flip a firm between the legacy per-case subscription rail and the
 * activity-driven monthly statement rail. The Cloud Function handles all
 * the orchestration (pausing/resuming the subscription, payment-method
 * checks, billing-event logging).
 *
 * billingTerms is required when billingMode === 'monthly_statement'.
 */
export async function setFirmBillingMode(
  firmId: string,
  billingMode: FirmBillingMode,
  billingTerms?: FirmBillingTerms
): Promise<{ success: boolean; billingMode: FirmBillingMode; billingTerms: FirmBillingTerms | null } | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      billingMode: FirmBillingMode;
      billingTerms: FirmBillingTerms | null;
    }>('setFirmBillingMode', { firmId, billingMode, billingTerms });
    return result;
  }, 'updating billing mode');
}

/**
 * Read the statementInvoices subcollection for a firm, ordered by period
 * end (newest first). Firestore rules permit:
 *  - firm staff (`belongsToFirm + isStaff`) for their own firm
 *  - platform admins for any firm
 *  - admin impersonation for the impersonated firm
 * Other callers get a permission error from Firestore, not an empty array.
 */
export interface AdminStatementInvoice {
  statementInvoiceId: string;
  stripeInvoiceId: string;
  firmId: string;
  periodKey: string;
  periodStart?: { seconds: number; nanoseconds: number } | Date | null;
  periodEnd?: { seconds: number; nanoseconds: number } | Date | null;
  dueDate?: { seconds: number; nanoseconds: number } | Date | null;
  totalCents: number;
  status: string;
  paidOutOfBandNote?: string;
  paidOutOfBandBy?: string;
  hostedInvoiceUrl?: string;
}

export async function getStatementInvoicesForFirm(
  firmId: string
): Promise<AdminStatementInvoice[] | undefined> {
  return withErrorHandling(async () => {
    const stmtRef = collection(db, `firms/${firmId}/statementInvoices`);
    const q = query(stmtRef, orderBy('periodEnd', 'desc'));
    const snapshot = await getDocs(q);

    const rows: AdminStatementInvoice[] = [];
    snapshot.forEach((d) => {
      const data = d.data();
      rows.push({
        statementInvoiceId: d.id,
        stripeInvoiceId: (data.stripeInvoiceId as string) ?? d.id,
        firmId: (data.firmId as string) ?? firmId,
        periodKey: (data.periodKey as string) ?? '',
        periodStart: data.periodStart ?? null,
        periodEnd: data.periodEnd ?? null,
        dueDate: data.dueDate ?? null,
        totalCents: (data.totalCents as number) ?? 0,
        status: (data.status as string) ?? 'unknown',
        paidOutOfBandNote: data.paidOutOfBandNote as string | undefined,
        paidOutOfBandBy: data.paidOutOfBandBy as string | undefined,
        hostedInvoiceUrl: data.hostedInvoiceUrl as string | undefined,
      });
    });
    return rows;
  }, 'fetching statement invoices');
}

/**
 * Admin-only — record that a statement invoice was paid outside of Stripe
 * (mailed check, wire, etc.). Stripe marks the invoice settled without
 * actually charging anything; the firm-side UI surfaces the admin's note
 * so attorneys can see how the payment was recorded.
 *
 * @warning The `note` field is stored unredacted on the firestore doc
 *   (logger redaction is for log calls, not firestore writes). When wiring
 *   up an admin UI for this endpoint, surface a helper-text hint:
 *   "Don't include client names, DOB, or other identifiers — describe the
 *   payment method only." The corresponding zod schema in
 *   functions/src/shared/schemas.ts carries the same rule via `.describe()`.
 */
export async function markStatementInvoicePaidOutOfBand(
  firmId: string,
  statementInvoiceId: string,
  note: string
): Promise<{ success: boolean; statementInvoiceId: string; status: string } | undefined> {
  return withErrorHandling(async () => {
    const result = await callCloudFunction<{
      success: boolean;
      statementInvoiceId: string;
      status: string;
    }>('markStatementInvoicePaidOutOfBand', { firmId, statementInvoiceId, note });
    return result;
  }, 'recording out-of-band payment');
}
